import type { RulesEndScreen } from '../hooks/useRulesBoardGame';

import './VifEndScreen.scss';

export interface VifEndScreenProps {
  copy: RulesEndScreen;
  /** Last game of the last round: Continue finishes the stage. */
  finalGame: boolean;
  preview: boolean;
  /** Awarded by the progress save, or `null` when none/not scored. */
  stickers?: number | null;
  /**
   * The game's save is still in flight, so there is nothing to move on to yet.
   * See the button note below.
   */
  saving?: boolean;
  /** Where the More Games button goes. Legacy `params.hubUrl`. */
  hubUrl?: string;
  onNext: () => void;
  onContinue: () => void;
}

/**
 * The post-game dialogue, rebuilt in the DOM from `gameFrame.endScreen`.
 *
 * Shown after every completed game (`framework/GameFrame.ts:339-373`). Which
 * button appears is decided across `GameFrame.endGame` and
 * `boardGameEngine.stopGame` (`:289-324`), and comes out as:
 *
 * | Situation                    | Button     |
 * | ---------------------------- | ---------- |
 * | Ordinary game finished       | Next       |
 * | Last game of the last round  | Continue   |
 * | Preview / practice mode      | More Games |
 *
 * There is deliberately no Home button: both engines switch the end screen's
 * More Games button off on init (`rulesBoardEngine.ts:217`) and never switch
 * it back on, so only the practice menu's copy of it is ever reachable.
 *
 * Next and Continue wait on the game's save. The canvas build gets that for
 * free by only enabling its button from the save response
 * (`GameFrame.onDatabasePost`); here the caller reports the write as `saving`.
 * It matters because the server decides the next position from the record that
 * write creates, so a player who leaves ahead of it is sent back to the game
 * they have just finished.
 */
export function VifEndScreen({
  copy,
  finalGame,
  preview,
  stickers,
  saving = false,
  hubUrl,
  onNext,
  onContinue,
}: VifEndScreenProps) {
  return (
    <div className="vif-end-screen" role="dialog" aria-modal="true">
      <div className="vif-end-screen__panel">
        <h2 className="vif-end-screen__message">{copy.message}</h2>
        <p className="vif-end-screen__detail">{copy.detail}</p>

        {stickers ? (
          <p className="vif-end-screen__stickers">
            You earned {stickers} {stickers > 1 ? 'stickers' : 'sticker'}
          </p>
        ) : null}

        {preview ? (
          <a className="vif-end-screen__button" href={hubUrl ?? '#'}>
            More Games
          </a>
        ) : (
          <button
            type="button"
            className="vif-end-screen__button"
            disabled={saving}
            aria-busy={saving}
            onClick={finalGame ? onContinue : onNext}
          >
            {finalGame ? 'Continue' : 'Next'}
          </button>
        )}
      </div>
    </div>
  );
}
